import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchRevenueDropdownData,
  filterRevenueData,
  updateRevenueShare,
} from "../services/revenue.api";
import {
  CropTypeDropdownItem,
} from "../types/serviceagreements";
import { Option } from "../screens/ServiceAgreements/types";

interface CalendarYearDropdownItem {
  id: number;
  name: string;
}

interface LandlordClientMappingDropdownItem {
  landLordPartnerMappingId: number;
  landLordName: string;
  partnerName: string;
  displayName: string;
}

interface RevenueState {
  calendarYears: CalendarYearDropdownItem[];
  cropType: CropTypeDropdownItem[];
  landlord: Option[];
  landlordClientMappings: LandlordClientMappingDropdownItem[];
  loading: boolean;
  error: string | null;
  revenueShares: any;
}

const initialState: RevenueState = {
  calendarYears: [],
  cropType: [],
  landlord: [],
  landlordClientMappings: [],
  loading: false,
  error: null,
  revenueShares: {},
};

export const fetchRevenueDropdown = createAsyncThunk(
  "revenue/dropdown/fetch",
  async (dropdownType: string) => {
    const response = await fetchRevenueDropdownData(dropdownType);
    return response.data;
  }
);

export const fetchFilteredRevenueShare = createAsyncThunk(
  "revenue/filter",
  async (value: any) => {
    const response = await filterRevenueData(value);
    return response.data;
  }
);

export const postUpdateRevenueShare = createAsyncThunk(
  "revenue/updateRevenueShare",
  async (value: any, { rejectWithValue }) => {
    try {
      const response = await updateRevenueShare(value);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update revenue data';
      return rejectWithValue(errorMessage);
    }
  }
);

const revenueSlice = createSlice({
  name: "revenue",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRevenueDropdown.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRevenueDropdown.fulfilled, (state, action) => {
        state.loading = false;
        if (action.meta.arg === "calendarYear") {
          state.calendarYears = action.payload;
        } else if (action.meta.arg === "cropType") {
          state.cropType = action.payload;
        } else if (action.meta.arg === "landlord") {
          state.landlord = action.payload;
        } else if (action.meta.arg === "landlordClientMapping") {
          state.landlordClientMappings = action.payload;
        }
      })
      .addCase(fetchRevenueDropdown.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch dropdown data.";
      })
      .addCase(fetchFilteredRevenueShare.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFilteredRevenueShare.fulfilled, (state, action) => {
        state.loading = false;
        state.revenueShares = action.payload;
      })
      .addCase(fetchFilteredRevenueShare.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch revenue data.";
      })
      .addCase(postUpdateRevenueShare.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postUpdateRevenueShare.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(postUpdateRevenueShare.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to update revenue data';
      });
  },
});

export default revenueSlice.reducer;